/**
 * awe-notify-me-component
 *
 * Custom element for the "Notify Me When Available" back-in-stock popup (PDP only).
 *
 * Responsibilities:
 *  1. Listen for Horizon's variant:update event to show/hide based on availability.
 *  2. Update the variant ID passed to the Klaviyo API when the variant changes.
 *  3. Validate the email input and enable/disable the submit button accordingly.
 *  4. Submit a Klaviyo back-in-stock subscription and show a success state.
 *
 * The element uses Horizon's dialog-component (dialog.js) for show/close logic.
 * No new dialog implementation is created here.
 */

const KLAVIYO_COMPANY_ID = 'MG6zzg';
const KLAVIYO_API_REVISION = '2024-06-15';
const KLAVIYO_BIS_URL = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;
const KLAVIYO_SUBSCRIBE_URL = `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;
const KLAVIYO_CONSENT_SOURCE = 'PDP Join the Waitlist';
// profile.subscriptions (marketing consent) is rejected with a 400 on older revisions like 2024-06-15.
const KLAVIYO_SUBSCRIBE_REVISION = '2025-01-15';

class AweNotifyMeComponent extends HTMLElement {
  constructor() {
    super();
    this._section = null;
    this._onVariantUpdate = this._onVariantUpdate.bind(this);
  }

  connectedCallback() {
    // Scope variant:update listener to the nearest Shopify section
    this._section = this.closest('.shopify-section');
    if (this._section) {
      this._section.addEventListener('variant:update', this._onVariantUpdate);
    }

    this._lastSuggestedEmail = null;
    this._bindEmailInput();
    this._bindSubmit();
  }

  disconnectedCallback() {
    if (this._section) {
      this._section.removeEventListener('variant:update', this._onVariantUpdate);
    }
  }

  /* ── Horizon variant:update handler ──────────────────────────────────── */

  _onVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    // Only respond to events for this product
    const productId = this.dataset.productId;
    const eventProductId = String(event.detail?.data?.productId || '');
    if (eventProductId && eventProductId !== productId) return;

    // Update stored variant ID for the Klaviyo submission
    this.dataset.variantId = String(variant.id);

    // Show when OOS, hide when available
    if (variant.available === false) {
      this.removeAttribute('hidden');
    } else {
      this.setAttribute('hidden', '');
      // Reset form state when variant becomes available (user may reopen later)
      this._resetForm();
      this._restoreAddToCart(event);
    }
  }

  /**
   * A page that lands on a sold out variant renders no Add to Cart
   * (blocks/buy-buttons.liquid), so product-form.js has nothing to update when an
   * available variant is picked. Bring the buttons in from the re-rendered section
   * (PDP buy box handoff 10/28, red 29: a sold out ?variant= link, then an in-stock size).
   * @param {Event} event - variant:update
   */
  _restoreAddToCart(event) {
    const block = this.closest('.buy-buttons-block');
    const buttons = block?.querySelector('.product-form-buttons');
    if (!buttons || buttons.querySelector('[ref="addToCartButton"]')) return;

    const fresh = event.detail?.data?.html?.querySelector('.buy-buttons-block .product-form-buttons');
    if (!fresh || !fresh.querySelector('[ref="addToCartButton"]')) return;

    buttons.replaceWith(document.importNode(fresh, true));
  }

  /* ── Email input validation ───────────────────────────────────────────── */

  _bindEmailInput() {
    const emailInput = this.querySelector('[data-ref="emailInput"]');
    const submitBtn = this.querySelector('[data-ref="submitButton"]');
    if (!emailInput || !submitBtn) return;

    emailInput.addEventListener('input', () => {
      if (this._validateEmail(emailInput.value)) {
        submitBtn.removeAttribute('disabled');
      } else {
        submitBtn.setAttribute('disabled', '');
      }
      this._lastSuggestedEmail = null;
      this._clearTypoMessage();
    });
  }

  /* ── Form submit ──────────────────────────────────────────────────────── */

  _bindSubmit() {
    const submitBtn = this.querySelector('[data-ref="submitButton"]');
    if (!submitBtn) return;

    submitBtn.addEventListener('click', () => {
      const emailInput = this.querySelector('[data-ref="emailInput"]');
      const email = emailInput ? emailInput.value.trim() : '';
      if (!this._validateEmail(email)) return;

      const typoCheck = window.AweEmailTypoCheck && window.AweEmailTypoCheck.check(email);

      if (typoCheck && typoCheck.type === 'block') {
        this._showTypoMessage(emailInput, 'Please enter a real email address.');
        return;
      }

      if (typoCheck && typoCheck.type === 'suggest' && email !== this._lastSuggestedEmail) {
        this._lastSuggestedEmail = email;
        this._showTypoSuggestion(emailInput, typoCheck.correctedEmail, () => {
          emailInput.value = typoCheck.correctedEmail;
          this._clearTypoMessage();
          this._lastSuggestedEmail = null;
          this._submitToKlaviyo(typoCheck.correctedEmail, this.dataset.variantId);
        });
        return;
      }

      this._lastSuggestedEmail = null;
      this._clearTypoMessage();
      this._submitToKlaviyo(email, this.dataset.variantId);
    });
  }

  /* ── Typo-check message UI ───────────────────────────────────────────── */

  _clearTypoMessage() {
    const el = this.querySelector('.awe-notify-me__typo-message');
    if (el) el.remove();
  }

  _showTypoMessage(emailInput, text) {
    this._clearTypoMessage();
    const el = document.createElement('p');
    el.className = 'awe-notify-me__typo-message';
    el.setAttribute('role', 'alert');
    el.textContent = text;
    emailInput.closest('.awe-notify-me__field').after(el);
  }

  _showTypoSuggestion(emailInput, correctedEmail, onAccept) {
    this._clearTypoMessage();
    const el = document.createElement('p');
    el.className = 'awe-notify-me__typo-message';
    el.setAttribute('role', 'alert');
    el.appendChild(window.AweEmailTypoCheck.buildSuggestionMarkup(correctedEmail, onAccept));
    emailInput.closest('.awe-notify-me__field').after(el);
  }

  _submitToKlaviyo(email, variantId) {
    const submitBtn = this.querySelector('[data-ref="submitButton"]');
    if (submitBtn) {
      submitBtn.setAttribute('disabled', '');
      submitBtn.textContent = '…';
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`
            }
          }
        }
      }
    };

    fetch(KLAVIYO_BIS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION
      },
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Klaviyo error: ${text}`);
        }
        // The back-in-stock alert is set; marketing consent is a separate, opt-in call.
        if (this._hasMarketingConsent()) {
          await this._subscribeToMarketing(email);
        }
        this._showSuccess();
      })
      .catch((err) => {
        console.error('[awe-notify-me] Klaviyo back-in-stock error:', err);
        // Restore button so the user can retry
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.textContent = submitBtn.dataset.label || 'Notify me when available';
        }
      });
  }

  /* ── Email marketing consent ──────────────────────────────────────────── */

  _hasMarketingConsent() {
    const consentInput = this.querySelector('[data-ref="consentInput"]');
    return Boolean(consentInput && consentInput.checked && this.dataset.consentListId);
  }

  /**
   * Subscribes the email to email marketing on the configured Klaviyo list
   * (settings.awe_notify_me_consent_list_id). Only called when the shopper ticked
   * the consent box. A failure here is logged but does not undo the waitlist signup.
   * @param {string} email
   */
  async _subscribeToMarketing(email) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO_CONSENT_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } }
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: this.dataset.consentListId } }
        }
      }
    };

    try {
      const res = await fetch(KLAVIYO_SUBSCRIBE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_SUBSCRIBE_REVISION
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        console.error('[awe-notify-me] Klaviyo subscription error:', await res.text());
      }
    } catch (err) {
      console.error('[awe-notify-me] Klaviyo subscription error:', err);
    }
  }

  /* ── UI state helpers ─────────────────────────────────────────────────── */

  _showSuccess() {
    const form = this.querySelector('[data-ref="form"]');
    const success = this.querySelector('[data-ref="success"]');
    if (form) form.classList.add('hidden');
    if (success) success.classList.remove('hidden');
  }

  _resetForm() {
    const form = this.querySelector('[data-ref="form"]');
    const success = this.querySelector('[data-ref="success"]');
    const emailInput = this.querySelector('[data-ref="emailInput"]');
    const submitBtn = this.querySelector('[data-ref="submitButton"]');

    if (form) form.classList.remove('hidden');
    if (success) success.classList.add('hidden');
    if (emailInput) emailInput.value = '';
    const consentInput = this.querySelector('[data-ref="consentInput"]');
    if (consentInput) consentInput.checked = false;
    if (submitBtn) {
      submitBtn.setAttribute('disabled', '');
      submitBtn.textContent = submitBtn.dataset.label || 'Notify me when available';
    }
    this._lastSuggestedEmail = null;
    this._clearTypoMessage();
  }

  /* ── Utilities ────────────────────────────────────────────────────────── */

  _validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email));
  }
}

if (!customElements.get('awe-notify-me-component')) {
  customElements.define('awe-notify-me-component', AweNotifyMeComponent);
}
